import * as React from 'react';
import { useUploadContent, IUploadConfig, IUploadOptions } from '@frontend/app/hooks/useUploadContent';

const DEFAULT_CONFIG: IUploadConfig = {
  serviceName: 'message',
  parentFolder: 'inline-images',
  isTemp: false,
  keepFileName: true,
};

const DEFAULT_OPTIONS: IUploadOptions = {
  imageCompressionEnabled: true,
  imageCompressionOptions: {
    maxWidthOrHeight: 1024,
    initialQuality: 0.8,
  },
};

export interface IImageUploadResult {
  url: string;
  name: string;
  size: number;
}

export const useImageUpload = (config?: Partial<IUploadConfig>, options?: IUploadOptions) => {
  const uploadConfig = React.useMemo(
    () => ({ ...DEFAULT_CONFIG, ...config }),
    [config]
  );

  const uploadOptions = React.useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...options }),
    [options]
  );

  const { upload, error, content, isUploading } = useUploadContent(uploadConfig);

  const uploadImage = React.useCallback(
    async (file: File): Promise<IImageUploadResult> => {
      const fileUrl = await upload(file, 'image', uploadOptions);

      return {
        url: fileUrl,
        name: file.name,
        size: file.size,
      };
    },
    [upload, uploadOptions]
  );

  const validateImageFile = React.useCallback((file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'Image file size must be less than 10MB';
    }

    return null;
  }, []);

  return {
    uploadImage,
    validateImageFile,
    isUploading,
    error,
    content,
  };
};
